import React, { useEffect, useRef, useState } from 'react';
import langeImg from '@/assets/quotes/lange.jpg';
import poundImg from '@/assets/quotes/pound.jpg';
import jobsImg from '@/assets/quotes/jobs.jpg';
import lessigImg from '@/assets/quotes/lessig.jpg';
import turingImg from '@/assets/quotes/turing.jpg';
import lovelaceImg from '@/assets/quotes/lovelace.jpg';
import russellImg from '@/assets/quotes/russell.jpg';
import gebruImg from '@/assets/quotes/gebru.jpg';
import holmesImg from '@/assets/quotes/holmes.jpg';

type Entry = {
  author: string;
  title: string;
  label: string;
  image: string;
  quote: string;
};

// Three thematic decks. Each card cycles through its deck of thinkers
// only while the visitor hovers (desktop) or taps (touch).
const decks: Entry[][] = [
  // Card 1 — AI Governance & Ethical Frameworks
  [
    {
      author: 'Christian Lous Lange',
      title: 'AI Governance',
      label: 'Ethical Frameworks',
      image: langeImg,
      quote: 'Technology is a useful servant but a dangerous master.',
    },
    {
      author: 'Stuart Russell',
      title: 'Human-Compatible AI',
      label: 'Alignment & Safety',
      image: russellImg,
      quote:
        'The real risk with AI isn\u2019t malice but competence \u2014 a superintelligent AI will be extremely good at accomplishing its goals, and if those goals aren\u2019t aligned with ours, we\u2019re in trouble.',
    },
    {
      author: 'Timnit Gebru',
      title: 'Algorithmic Accountability',
      label: 'Bias & Fairness',
      image: gebruImg,
      quote:
        'You can\u2019t have ethical AI that isn\u2019t inclusive. And whoever is creating the technology is setting the standards.',
    },
  ],
  // Card 2 — Adaptive Justice & Regulatory Evolution
  [
    {
      author: 'Roscoe Pound',
      title: 'Adaptive Justice',
      label: 'Regulatory Evolution',
      image: poundImg,
      quote: 'The law must be stable, but it must not stand still.',
    },
    {
      author: 'Lawrence Lessig',
      title: 'Code as Law',
      label: 'Cyber Jurisprudence',
      image: lessigImg,
      quote:
        'Code is law. In cyberspace, the architecture of the network regulates behavior as powerfully as any statute.',
    },
    {
      author: 'Oliver Wendell Holmes Jr.',
      title: 'Living Law',
      label: 'Judicial Pragmatism',
      image: holmesImg,
      quote:
        'The life of the law has not been logic; it has been experience \u2014 the felt necessities of the time.',
    },
  ],
  // Card 3 — Innovation, Computing & the Future
  [
    {
      author: 'Steve Jobs',
      title: 'Future-Ready',
      label: 'Legal Innovation',
      image: jobsImg,
      quote: 'Innovation distinguishes between a leader and a follower.',
    },
    {
      author: 'Alan Turing',
      title: 'Machine Intelligence',
      label: 'Foundations of AI',
      image: turingImg,
      quote:
        'We can only see a short distance ahead, but we can see plenty there that needs to be done.',
    },
    {
      author: 'Ada Lovelace',
      title: 'Algorithmic Vision',
      label: 'Computing Pioneers',
      image: lovelaceImg,
      quote:
        'The Analytical Engine weaves algebraic patterns just as the Jacquard loom weaves flowers and leaves.',
    },
  ],
];

const READ_MS = 10000; // reading time between flips while engaged
const FLIP_MS = 1400;  // slower, smoother flip
const FIRST_FLIP_MS = 600; // quick first flip so visitor sees it respond

const STAGGER_MS = 7000; // delay between cards starting their flip cycle

const QuoteCard = ({ deck, indexOffset }: { deck: Entry[]; indexOffset: number }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1 % deck.length);
  const [flipped, setFlipped] = useState(false);
  const [inView, setInView] = useState(false);
  const hasFlippedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const front = deck[currentIndex];
  const back = deck[nextIndex];

  // Observe whether the card is scrolled into view; flips run only while visible.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setInView(entry.isIntersecting));
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Auto-flip while the card is in view. First flip is quick so visitors
  // notice it as they scroll past; subsequent flips allow reading time.
  useEffect(() => {
    if (!inView) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    const baseDelay = hasFlippedRef.current ? READ_MS : FIRST_FLIP_MS;
    const delay = baseDelay + (hasFlippedRef.current ? 0 : indexOffset * STAGGER_MS);
    timerRef.current = window.setTimeout(() => {
      hasFlippedRef.current = true;
      setFlipped((f) => !f);
    }, delay);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [flipped, inView]);

  // After the flip animation finishes, prepare the now-hidden face with the next quote.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (flipped) {
        setCurrentIndex((nextIndex + 1) % deck.length);
      } else {
        setNextIndex((currentIndex + 1) % deck.length);
      }
    }, FLIP_MS);
    return () => window.clearTimeout(t);
  }, [flipped]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      className="group [perspective:1200px]"
    >
      <div
        className="relative h-[460px] w-full [transform-style:preserve-3d]"
        style={{
          transition: `transform ${FLIP_MS}ms ease-in-out`,
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        <div className="absolute inset-0 [backface-visibility:hidden]">
          <CardFace entry={front} />
        </div>
        <div className="absolute inset-0 [transform:rotateY(180deg)] [backface-visibility:hidden]">
          <CardFace entry={back} />
        </div>
      </div>
    </div>
  );
};

const CardFace = ({ entry }: { entry: Entry }) => (
  <div className="h-full w-full text-center bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl shadow-md p-8 border border-primary/10 flex flex-col items-center">
    <img
      src={entry.image}
      alt={`Portrait of ${entry.author}`}
      loading="lazy"
      width={96}
      height={96}
      className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-primary/20 shadow-sm"
    />
    <blockquote className="text-gray-600 italic text-base md:text-lg mb-4 leading-relaxed flex-1 flex items-center justify-center">
      "{entry.quote}"
    </blockquote>
    <p className="text-sm text-gray-500 mb-4">— {entry.author}</p>
    <div className="border-t border-primary/20 pt-4 w-full">
      <div className="text-xl md:text-2xl font-bold text-primary mb-1">{entry.title}</div>
      <div className="text-gray-600 font-medium uppercase tracking-wide text-xs">
        {entry.label}
      </div>
    </div>
  </div>
);

const StatsSection = () => {
  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {decks.map((deck, i) => (
            <QuoteCard key={i} deck={deck} indexOffset={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
